import { CalendarDay } from './calendar-day';

/*

  Function to calculate the displayed
  days of the month on a given calendar date picker.

  return value is an date ordered array of objects which contain a day of the month
  and metadata for that day based on input information.

  sample partial return:

  [
    {
      dayOfTheMonth: 30, //The actual number to show on the calendar
      monthNumber: 8, //aligns with javascript month index (october in this case) [0 = Jan, 1 = Feb, ... , 11 = Dec]
      year: 2017,
      outOfRange: false,
      monthRelativePosition: 'PREVIOUS',
      // 'PREVIOUS', 'CURRENT' or 'NEXT' defines which month this particular day of the month lies relative to the passed, year and month.
      dayOfTheWeek: 6, // aligns with javascript day index [0 = sunday, 1 = monday ... 6 = saturday]
    },
    {
      dayOfTheMonth: 1, //The actual number to show on the calendar
      monthNumber: 9, //aligns with javascript month index (october in this case) [0 = Jan, 1 = Feb, ... , 11 = Dec]
      year: 2017,
      outOfRange: false,
      monthRelativePosition: 'CURRENT',
      // 'PREVIOUS', 'CURRENT' or 'NEXT' defines which month this particular day of the month lies relative to the passed, year and month.
      dayOfTheWeek: 0, // aligns with javascript day index [0 = sunday, 1 = monday ... 6 = saturday]
    }
  ]

*/

const monthRelativePosition = {
  PREVIOUS: 'PREVIOUS',
  CURRENT: 'CURRENT',
  NEXT: 'NEXT',
};

/*
  Parameters:
    year: the year of the month you want to calculate
    month: the month number of the month you want to calculate (uses javascript month indexing number)
    firstDayOftheWeek: the day of the week to start the calendar at (defaults to 0)
    minDate: allows you to specify a min date in which if the day of the month falls before, it will be marked with a outOfRange flag
    maxDate: allows to to specify a max date in which if the day of the month falls after, it will be marked with a outOfRange flag
*/
function calculateDaysInMonth(year, month, options): CalendarDay[] {
  let results: CalendarDay[] = [];
  let { minDate, maxDate, firstDayOfTheWeek } = <any>(options || {});
  // default values
  if (minDate) {
    minDate = new Date(
      minDate.getFullYear(),
      minDate.getMonth(),
      minDate.getDate()
    );
  } else {
    minDate = new Date(-8640000000000000);
  }
  if (maxDate) {
    maxDate = new Date(
      maxDate.getFullYear(),
      maxDate.getMonth(),
      maxDate.getDate()
    ); // max date
  } else {
    maxDate = new Date(8640000000000000);
  }
  firstDayOfTheWeek = firstDayOfTheWeek || 0; // default starts at sunday
  const lastDayOfTheWeek = mod(firstDayOfTheWeek + 6, 7); // default ends at saturday

  // targetDate will hold the primary year/month we want to
  // calculate the days for.
  const targetMonth = new Date(year, month);

  // Also need the date objects for the previous and next months
  // calculated from the target month date object
  const previousMonth = new Date(new Date(year, month).setDate(0));
  const nextMonth = addMonths(new Date(year, month), 1);

  const firstDayOfTheWeekForTheCurrentMonth = targetMonth.getDay();

  // Grab the overflow dates from the previous month.
  results = results.concat(
    calculateOverflowDaysFromPreviousMonth(
      previousMonth.getFullYear(),
      previousMonth.getMonth(),
      minDate,
      maxDate,
      firstDayOfTheWeek,
      firstDayOfTheWeekForTheCurrentMonth - 1 // see example 1.a.
    )
  );

  // Grab the calendar dates for the current month
  results = results.concat(
    calculateDaysForCurrentMonth(
      targetMonth.getFullYear(),
      targetMonth.getMonth(),
      minDate,
      maxDate
    )
  );

  // NOTE: this assumes we always want to display 6 rows of 7 days.
  // we may want to make this adjustable in the future.
  const numberOfNextMonthsDaysToCalculate = 7 * 6 - results.length;
  assert(
    numberOfNextMonthsDaysToCalculate >= 0,
    "Error: number of next month's days to calculate was less than zero"
  );

  // Grab the calendar dates for the next month up to an extra week.
  results = results.concat(
    calculateOverflowDaysFromNextMonth(
      nextMonth.getFullYear(),
      nextMonth.getMonth(),
      minDate,
      maxDate,
      numberOfNextMonthsDaysToCalculate
    )
  );

  return results;
}

/*

  This function loops in reverse starting from oneday before the last day
  of the target month, to the first day of the week for the last week of the previous month,
  puts the calendar day objects into an array in ascending date order and returns that array.

  example 1.a.
    assume first day of target month starts on thursday.
    and our starting day of the week for each week in the calendar is sunday

     0  1  2  3   4  5 6
    (S) M  T  W  [T] F S
    28  29 30 31  1  2 3
    this means the overflow days from the previous month are 0-3 inclusive.
    which is why we calculate the getDay() function for the current month at it's first date (4),
    subtract 1 (3), and pass it into the overflow from previous month function.

    This would also apply if we wanted our calendar to start on monday or tuesday or
    any day for that matter.

    In the case where the first day of the week for the current month is the same
    as our defined first day of the month
      0   1 2 3 4 5 6
    ([S]) M T W T F S
      1   2 3 4 5 6 7

    -1 will be passed to the function and the forloop inside will not add any days
    from the previous month.

*/
function calculateOverflowDaysFromPreviousMonth(
  year,
  month,
  minDate,
  maxDate,
  firstDayOfTheWeek,
  lastDayOfTheWeek
): CalendarDay[] {
  const results: CalendarDay[] = [];

  // 0 as the third parameter, sets the date to the last day of the previous month
  let date = new Date(year, month + 1, 0);

  for (let i = lastDayOfTheWeek; i >= firstDayOfTheWeek; i--) {
    assert(date.getDay() === i, 'day of the week and day index mismatch');

    results.unshift({
      dayOfTheMonth: date.getDate(),
      monthNumber: month,
      year: year,
      outOfRange: date < minDate || date > maxDate,
      monthRelativePosition: monthRelativePosition.PREVIOUS,
      dayOfTheWeek: date.getDay(),
      date: new Date(date),
    });
    date = new Date(date.setDate(date.getDate() - 1));
  }
  return results;
}

/*
  Calculates the calendar days for the current month.
*/
function calculateDaysForCurrentMonth(
  year,
  month,
  minDate,
  maxDate
): CalendarDay[] {
  const results: CalendarDay[] = [];
  let date = new Date(year, month);
  // let dateIndex = 1; // starting **date** = 1, starting day = 0... (go JavaScript!).

  // While our date is in the current month.
  // Javascript should increment the month if we
  // increase the date past the month's total number
  // of days.
  // e.g setDate(32) will move us to the next month.
  while (date.getMonth() === month) {
    results.push({
      dayOfTheMonth: date.getDate(),
      monthNumber: month,
      year: year,
      outOfRange: date < minDate || date > maxDate,
      monthRelativePosition: monthRelativePosition.CURRENT,
      dayOfTheWeek: date.getDay(),
      date: new Date(date),
    });

    // date = date.setDate(++dateIndex);
    date = addDays(date, 1);
  }

  return results;
}

/*
    {
      dayOfTheMonth: 1, //The actual number to show on the calendar
      monthNumber: 9, //aligns with javascript month index (october in this case) [0 = Jan, 1 = Feb, ... , 11 = Dec]
      year: 2017,
      outOfRange: false,
      monthRelativePosition: 'CURRENT',
      // 'PREVIOUS', 'CURRENT' or 'NEXT' defines which month this particular day of the month lies relative to the passed, year and month.
      dayOfTheWeek: 0, // aligns with javascript day index [0 = sunday, 1 = monday ... 6 = saturday]
    }

*/
function calculateOverflowDaysFromNextMonth(
  year,
  month,
  minDate,
  maxDate,
  totalNumberOfDays
): CalendarDay[] {
  const results: CalendarDay[] = [];
  let date = new Date(year, month);
  for (let i = 0; i < totalNumberOfDays; i++) {
    results.push({
      dayOfTheMonth: date.getDate(),
      monthNumber: month,
      year: year,
      outOfRange: date < minDate || date > maxDate,
      monthRelativePosition: monthRelativePosition.NEXT,
      dayOfTheWeek: date.getDay(),
      date: new Date(date),
    });
    date = addDays(date, 1);
  }
  return results;
}

/*
 add days, such that if we go into the next month
 the date's month will increment

*/
function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/*
 subtract days, such that if we go into the previous month
 the date's month will decrement

*/
function subtractDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

/*
 add months, such that if we go into the next year
 the date's year will increment.

 function adapted from StackOverflow.

 Answer:
 https://stackoverflow.com/a/35299103/2313300
 Answered by:
 https://stackoverflow.com/users/3174665/amarcruz

*/
function addMonths(date, count) {
  if (date && count) {
    let m;
    const d = (date = new Date(+date)).getDate();
    date.setMonth(date.getMonth() + count, 1);
    m = date.getMonth();
    date.setDate(d);
    if (date.getMonth() !== m) {
      date.setDate(0);
    }
  }
  return date;
}

/*
  Javascript does not support negative modulo.

  function adapted from StackOverflow.

  Answer:
  https://stackoverflow.com/a/4467559/2313300
  Answered by:
  https://stackoverflow.com/users/176291/enrique

*/
function mod(number, modValue) {
  return ((number % modValue) + modValue) % modValue;
}

function assert(statement, messageIfInvalid) {
  if (!statement) {
    // console.error(messageIfInvalid);
    throw new Error(`ASSERTION FAILURE: ${messageIfInvalid}`);
  }
}

export { calculateDaysInMonth, monthRelativePosition, addMonths };
